/**
 * SDK 硬件缓存层 - CacheKey 生成
 *
 * 提供 `(address, length)` → `CacheKey` 的纯函数实现。
 * 详见 design.md `Data Models §1`。
 *
 * 关键约束 (design `Data Models §1`):
 * - `(address, length)` 是缓存条目的唯一决定因素;同一地址不同 length,或同 length
 *   不同地址,均视为不同缓存条目。
 * - 不引入 `connectionType` 维度: 协议假设有线/无线对同一段地址返回相同字节内容。
 * - 不引入设备 ID: 每个 `BaseDevice` 持有一个独立的 `HardwareCache` 实例,
 *   key 在实例内部唯一即可。
 * - 字符串拼接而非对象: `Map<string, ...>` 的性能与简洁性优于 `Map<{addr,len}, ...>`,
 *   并且方便日志输出。
 *
 * 本文件不依赖任何运行时模块,仅依赖类型导入,可被任意 Phase 单独引用。
 *
 * @packageDocumentation
 */

import type { CommonAddress } from '../types/protocol'
import type { CacheKey } from './types'

/**
 * 生成 CacheKey。
 *
 * 规则 (与 design `Data Models §1` 一致):
 * - address 部分: 字节用 `'-'` 连接的十进制字符串
 *   (例: `[0x10, 0x01, 0xa0, 0x00]` → `"16-1-160-0"`)
 * - length 部分: 直接拼接十进制
 * - 中间用 `':'` 分隔
 *
 * 示例: `address=[0x10, 0x01, 0xa0, 0x00], length=16` → `"16-1-160-0:16"`。
 *
 * 纯函数,无副作用。相同入参始终返回相同字符串(幂等)。
 *
 * @param address 4 字节硬件地址 (`CommonAddress = number[]`)。允许任意长度,空数组合法。
 * @param length 期望读取的字节长度。允许 0(协议层何时合法由调用方负责)。
 * @returns 形如 `"<b0>-<b1>-...-<bN>:<length>"` 的缓存键字符串。
 */
export function makeKey(address: CommonAddress, length: number): CacheKey {
  return `${address.join('-')}:${length}`
}

/**
 * 将 `CommonAddress`(number[] 最多 4 字节)转换为单个 32 位整数,采用协议地址的小端序。
 *
 * 规则:
 * - 最低有效字节在数组前端(little-endian)
 * - 地址不足 4 字节时,高位补零
 * - 例: `[0x00, 0xa0, 0x01, 0x10]` → `0x1001a000`
 *
 * 用于 `TaggedCache.invalidateByAddressRange` 的地址区间数值化参数。
 *
 * @param address 硬件地址 (`CommonAddress = number[]`),最多 4 字节。
 * @returns 32 位无符号整数(使用 `>>> 0` 确保为正数)。
 */
export function addrToNumber(address: CommonAddress): number {
  // 取前 4 字节,不足的高位补零
  const b0 = address[0] ?? 0
  const b1 = address[1] ?? 0
  const b2 = address[2] ?? 0
  const b3 = address[3] ?? 0
  return (b0 | (b1 << 8) | (b2 << 16) | (b3 << 24)) >>> 0
}
